// React
import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import * as XLSX from "xlsx"

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"

// MUI Components
import { Box, Grid, Typography, Card } from "@mui/material"
import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import GppGoodIcon from "@mui/icons-material/GppGood"
import Button from "@mui/material/Button"

import RoleType from "../../../@types/RoleType"

// MUI Icon
import SearchIcon from "@mui/icons-material/Search"

import dayjs from "dayjs"

// Styles
import theme from "../../../styles/theme"

import {
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueGetterParams,
  GridRowSelectionModel,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  DataGrid,
  enUS,
} from "@mui/x-data-grid"
// Utils
import { useTranslation } from "react-i18next"

// Types
import { thTHGrid } from "../../../@types/DataGridLocaleText"

// Services
import { getPDPAUserPagingData } from "../../../services/PDPAService"

// ----- Types -----
interface PDPAUserRow {
  id: string
  pdpaVersionId?: string
  userId: string
  created_date?: string
  created_by?: string
  modified_date?: string
  modified_by?: string
  actionType?: string
  // relations (populated by backend join)
  user?: {
    employee_id?: string
    first_name_th?: string
    last_name_th?: string
    email?: string
  }
  PDPAVersion?: {
    version?: string
  }
}

const PDPAUserPaging = () => {
  const { t, i18n } = useTranslation()
  const userRoleType = useAppSelector((state) => state.user.roletype)

  const navigate = useNavigate()

  const styleCard = {
    borderRadius: "0.5rem",
    height: "100%",
    p: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  }

  const [pdpaData, setPdpaData] = useState<PDPAUserRow[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  })
  const [totalRows, setTotalRows] = useState<number>(0)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  )
  const prevSelectionRef = useRef<GridRowSelectionModel>([])

  const handleExportExcel = () => {
    const selectedRows =
      selectionModel.length > 0
        ? pdpaData.filter((row) => selectionModel.includes(row.id))
        : pdpaData

    const exportData = selectedRows.map((row) => ({
      [t("Action Type")]: row.actionType ?? "-",
      [t("Full Name")]: row.user
        ? `${row.user.first_name_th ?? ""} ${
            row.user.last_name_th ?? ""
          }`.trim() || "-"
        : "-",
      [t("PDPA Version")]: row.PDPAVersion?.version ?? row.pdpaVersionId ?? "-",
      [t("consent date/time")]: row.created_date
        ? dayjs(row.created_date).format("DD/MM/YYYY HH:mm")
        : "-",
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "PDPA Users")

    const fileName = `PDPA_Users_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`
    XLSX.writeFile(workbook, fileName)
  }

  const fetchPDPAUserData = async (page: number, pageSize: number) => {
    setLoading(true)
    const pagePro = page + 1
    try {
      const filterData = {
        page: pagePro,
        pageSize,
        query: searchQuery,
      }
      const response = await getPDPAUserPagingData(filterData)

      if (response.data?.success) {
        setPdpaData(response.data?.PDPAUsers?.data ?? [])
        setTotalRows(response.data?.PDPAUsers?.total ?? 0)
      }
    } catch (error) {
      navigate("/servererror", { replace: true })
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 300)
    }
  }

  // Function to handle row selection changes
  const handleSelectionModelChange = (
    newSelectionModel: GridRowSelectionModel
  ) => {
    // Get the previous selection
    const prevSelection = prevSelectionRef.current

    // Find IDs that were in the previous selection but not in the new one (unchecked rows)
    const removedIds = prevSelection.filter(
      (id) => !newSelectionModel.includes(id)
    )

    // Find IDs that are in the new selection but weren't in the previous one (newly checked rows)
    const addedIds = newSelectionModel.filter(
      (id) => !prevSelection.includes(id)
    )

    // if (
    //   removedIds.length > 0 ||
    //   newSelectionModel.length < selectionModel.length
    // ) {
    //   handleRowsUnchecked(removedIds)
    // }

    // if (
    //   addedIds.length > 0 ||
    //   selectionModel.length < newSelectionModel.length
    // ) {
    //   // const newAddId =
    //   //   addedIds.length > 0
    //   //     ? addedIds
    //   //     : [newSelectionModel[newSelectionModel.length - 1]]

    //   // setLoadingRows(true)
    //   // if (selectionModel.length < 10) {
    //   //   handleRowsChecked(newAddId)
    //   // } else {
    //   //   alertSwal("text_limit_10_user_apply_job", addedIds)
    //   // }
    // }

    // Update the selection model state
    setSelectionModel(newSelectionModel)

    prevSelectionRef.current = newSelectionModel
  }

  //  const handleRowsUnchecked = (removedIds: GridRowSelectionModel) => {
  //     setLoadingRows(false)
  //     setRowsData((prevItems) =>
  //       prevItems.filter((item) => !removedIds.includes(item.app_form_status_id))
  //     )
  //   }

  useEffect(() => {
    fetchPDPAUserData(paginationModel.page, paginationModel.pageSize)
  }, [paginationModel.page, paginationModel.pageSize, searchQuery])

  // NOTE: CustomToolbar — ซ้ำกันทุกหน้าที่มี DataGrid (37+ ไฟล์) ควรย้ายไปรวมใน components/common/DataGridToolbar.tsx
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "actionType",
      headerName: `${t("Action Type")}`,
      headerAlign: "center",
      width: 130,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const action = params.row.actionType
        if (!action) return "-"
        // const isAccept =
        //   action.toLowerCase() === "accept" ||
        //   action.toLowerCase() === "accepted"
        return (
          <Chip
            variant="outlined"
            color="success"
            label={action}
            size="small"
          />
        )
      },
    },
    // {
    //   field: "employeeId",
    //   headerName: `${t("Employee ID")}`,
    //   width: 130,
    //   valueGetter: (params: GridCellParams) =>
    //     params.row.user?.employee_id ?? params.row.userId,
    //   renderCell: (params: GridCellParams) =>
    //     params.row.user?.employee_id ?? params.row.userId ?? "-",
    // },
    {
      field: "fullName",
      headerName: `${t("Full Name")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => {
        const u = params.row.user
        if (!u) return "-"
        return `${u.first_name_th ?? ""} ${u.last_name_th ?? ""}`.trim() || "-"
      },
      renderCell: (params: GridCellParams) => {
        const u = params.row.user
        if (!u) return "-"
        return `${u.first_name_th ?? ""} ${u.last_name_th ?? ""}`.trim() || "-"
      },
    },
    // {
    //   field: "email",
    //   headerName: `${t("Email")}`,
    //   width: 220,
    //   valueGetter: (params: GridCellParams) => params.row.user?.email,
    //   renderCell: (params: GridCellParams) =>
    //     params.row.user?.email ?? "-",
    // },
    {
      field: "pdpaVersion",
      headerName: `${t("PDPA Version")}`,
      width: 150,
      valueGetter: (params: GridCellParams) =>
        params.row.PDPAVersion?.version ?? params.row.pdpaVersionId,
      renderCell: (params: GridCellParams) =>
        params.row.PDPAVersion?.version ?? params.row.pdpaVersionId ?? "-",
    },
    {
      field: "created_date",
      headerName: `${t("consent date/time")}`,
      width: 170,
      valueGetter: (params: GridCellParams) =>
        params.row.created_date ? Date.parse(params.row.created_date) : null,
      renderCell: (params: GridCellParams) =>
        params.row.created_date
          ? dayjs(params.row.created_date).format("DD/MM/YYYY HH:mm")
          : "-",
    },
    // {
    //   field: "created_by",
    //   headerName: `${t("Created By")}`,
    //   width: 160,
    //   valueGetter: (params: GridCellParams) => params.row.created_by,
    //   renderCell: (params: GridCellParams) =>
    //     params.row.created_by ?? "-",
    // },
    // {
    //   field: "modified_date",
    //   headerName: `${t("Modified Date")}`,
    //   width: 170,
    //   valueGetter: (params: GridCellParams) =>
    //     params.row.modified_date ? Date.parse(params.row.modified_date) : null,
    //   renderCell: (params: GridCellParams) =>
    //     params.row.modified_date
    //       ? dayjs(params.row.modified_date).format("DD/MM/YYYY HH:mm")
    //       : "-",
    // },
    // {
    //   field: "modified_by",
    //   headerName: `${t("Modified By")}`,
    //   width: 160,
    //   valueGetter: (params: GridCellParams) => params.row.modified_by,
    //   renderCell: (params: GridCellParams) =>
    //     params.row.modified_by ?? "-",
    // },
  ]

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "70px",
        gap: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "70px",
          width: "100%",
        }}
      >
        {" "}
        <Grid
          container
          spacing={2}
          sx={{ width: "100%", alignItems: "center" }}
        >
          <Grid item xs={12} lg={9}>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {t("PDPA User")}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            lg={2}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              disabled={pdpaData.length === 0}
              sx={{
                backgroundColor: theme.palette.button.main,
                color: "white",
                "&:hover": {
                  backgroundColor: "#0288d1",
                },
                minHeight: "42px",
                flex: "1 1 auto",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
              onClick={handleExportExcel}
            >
              {selectionModel.length > 0
                ? `${t("Export Excel")} (${selectionModel.length})`
                : t("Export Excel")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: "100%", marginTop: 3, paddingX: 3 }}>
        {/* Search */}
        {/* <Box>
          {userRoleType === RoleType.Super_Admin ? (
            // Case 1: Super_Admin (3 buttons: 2 on first line, 1 on second line)
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Button
                  variant="contained"
                  disabled={pdpaData.length === 0}
                  sx={{
                    backgroundColor: theme.palette.button.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#0288d1",
                    },
                    minHeight: "42px",
                    flex: "1 1 auto",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                  onClick={handleExportExcel}
                >
                  {selectionModel.length > 0
                    ? `${t("Export Excel")} (${selectionModel.length})`
                    : t("Export Excel")}
                </Button>
              </Box>
            </>
          ) : (
            <Box> </Box>
          )}
        </Box> */}
        <Box sx={{ width: "100%", paddingBottom: "20px" }}>
          <Grid container spacing={3} sx={{ marginTop: 1 }}>
            <Grid item xs={12}>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  label={t("Search")}
                  placeholder={t("text_input_search")!}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Summary Card */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={styleCard}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <GppGoodIcon
                    sx={{
                      color: "#42A5F5",
                      fontSize: "2rem",
                      backgroundColor: "#E3F2FD",
                      borderRadius: "50%",
                      p: 1,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: { xs: "12px", sm: "14px" }, opacity: 0.8 }}
                  >
                    {t("Total PDPA Records")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                  <Typography
                    sx={{ fontSize: { xs: 32, sm: 40 }, fontWeight: "bold" }}
                  >
                    {totalRows}
                  </Typography>
                  <Typography
                    sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                    gutterBottom
                  >
                    {t("records")}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* DataGrid */}
        <Box display="inline-grid" sx={{ width: "100%" }}>
          <DataGrid
            disableColumnMenu
            getRowId={(row) => row.id}
            rows={pdpaData}
            columns={columns}
            slots={{ toolbar: CustomToolbar }}
            pagination
            paginationMode="server"
            rowCount={Number(totalRows) || 0}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              fetchPDPAUserData(model.page, model.pageSize)
            }}
            pageSizeOptions={[10, 20, 30, 40, 50]}
            // disableRowSelectionOnClick
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionModelChange}
            localeText={
              i18n.language === "th"
                ? thTHGrid
                : enUS.components.MuiDataGrid.defaultProps.localeText
            }
          />
        </Box>
      </Box>
    </Box>
  )
}

export default PDPAUserPaging
